/**
 * MS-533 — rental agreement acceptance modal, ported from msool-frontend's
 * templates/dream/components/booking/RentalAgreementModal.tsx.
 *
 * The text comes ENTIRELY from the backend, which resolves the approved document's placeholders
 * for this specific reservation. This component must never substitute placeholders, translate
 * the body, or fall back to hardcoded terms: what the guest reads here is byte-identical to what
 * is recorded as consented, and anything else would mean recording consent to a document nobody
 * saw. Title, consent label and CTA label are server-supplied for the same reason — republishing
 * changes them without a deploy.
 */
import { useEffect, useMemo, useState } from "react";
import {
  AgreementModalOverlay,
  AgreementModalCard,
  AgreementModalHeader,
  AgreementModalTitle,
  AgreementModalClose,
  AgreementModalBody,
  AgreementHeading,
  AgreementParagraph,
  AgreementModalFooter,
  AgreementConsentLabel,
  AgreementActions,
  AgreementPrimaryButton,
  AgreementSecondaryButton,
  AgreementStateText,
} from "./styles";
import { parseAgreementBody } from "./agreementBody";
import type { RentalAgreementDocument } from "@services";

interface RentalAgreementModalProps {
  open: boolean;
  agreement: RentalAgreementDocument | null;
  isLoading?: boolean;
  /** `blocked` is terminal (unverified tourism permit) — no retry is offered. */
  loadError?: { blocked: boolean } | null;
  isSubmitting?: boolean;
  onAccept: () => void;
  onClose: () => void;
}

const RentalAgreementModal = ({
  open,
  agreement,
  isLoading,
  loadError,
  isSubmitting,
  onAccept,
  onClose,
}: RentalAgreementModalProps) => {
  const [consented, setConsented] = useState(false);

  // Consent is per-viewing: reopening must present an unchecked box, so a guest who closed
  // without agreeing is never carried forward by a stale tick.
  useEffect(() => {
    if (!open) setConsented(false);
  }, [open]);

  const lines = useMemo(
    () => (agreement ? parseAgreementBody(agreement.body) : []),
    [agreement]
  );

  if (!open) return null;

  const canSubmit = consented && !!agreement && !isSubmitting;

  return (
    <AgreementModalOverlay onClick={onClose}>
      <AgreementModalCard onClick={(e) => e.stopPropagation()}>
        <AgreementModalHeader>
          <AgreementModalTitle>
            {agreement?.title ?? "اتفاقية الإيجار"}
          </AgreementModalTitle>
          <AgreementModalClose type="button" onClick={onClose} aria-label="إغلاق">
            ×
          </AgreementModalClose>
        </AgreementModalHeader>

        <AgreementModalBody>
          {isLoading && <AgreementStateText>جارٍ تحميل الاتفاقية…</AgreementStateText>}

          {loadError && (
            <AgreementStateText>
              {loadError.blocked
                ? "لا يمكن إصدار اتفاقية إيجار كاملة لهذا العقار حالياً، لذلك لا يمكن إتمام الدفع. يرجى التواصل مع المضيف."
                : "تعذر تحميل الاتفاقية. يرجى المحاولة لاحقاً."}
            </AgreementStateText>
          )}

          {!isLoading &&
            !loadError &&
            lines.map((line) =>
              line.isHeading ? (
                <AgreementHeading key={line.key}>{line.text}</AgreementHeading>
              ) : (
                <AgreementParagraph key={line.key}>{line.text}</AgreementParagraph>
              )
            )}
        </AgreementModalBody>

        {agreement && !loadError && (
          <AgreementModalFooter>
            <AgreementConsentLabel>
              <input
                type="checkbox"
                checked={consented}
                onChange={(e) => setConsented(e.target.checked)}
              />
              {agreement.consentLabel}
            </AgreementConsentLabel>
            <AgreementActions>
              <AgreementPrimaryButton
                type="button"
                disabled={!canSubmit}
                onClick={onAccept}
              >
                {isSubmitting ? "جارٍ التوقيع…" : agreement.ctaLabel}
              </AgreementPrimaryButton>
              <AgreementSecondaryButton type="button" onClick={onClose}>
                إغلاق
              </AgreementSecondaryButton>
            </AgreementActions>
          </AgreementModalFooter>
        )}
      </AgreementModalCard>
    </AgreementModalOverlay>
  );
};

export default RentalAgreementModal;
